import {resolve} from "../../../container";
import {ResizeObserverFactory} from "../../../common/observation";
import {customElement} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {Deferred} from "../../../common/utils/promises";
import {PausableVideoLoop} from "../video";
import type {Maybe} from "../../../bootstrap/common/types";
import {HeroTeaserImageReadyHandler} from "./HeroTeaserImageReadyHandler";
import Styles from "./heroteaserVideoLoop.lit.scss";


export class EopHeroTeaser extends HTMLElement {

    public connectedCallback(): void {
        const imageReadyHandler = new HeroTeaserImageReadyHandler(".hero-teaser-image")
            .onImageReady(() => this.triggerAnimation())
            .installOn(this);

        const videoElements = this.querySelectorAll<EopHeroTeaserVideoLoop>("eop-heroteaser-video-loop");
        for (const videoElement of videoElements) {
            videoElement.whenVisuallyReady().then(() => imageReadyHandler.manuallyTrigger());
        }

        if (!imageReadyHandler.hasImageElementsToObserve() && videoElements.length === 0) {
            imageReadyHandler.manuallyTrigger();
        }
    }

    private triggerAnimation(): void {
        this.classList.remove("image-loading");
    }
}

export class EopImpulse extends HTMLElement {

    private resizeObserver: Maybe<ResizeObserver>;

    public constructor(private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory)) {
        super();
    }

    public connectedCallback(): void {
        this.resizeObserver = this.resizeObserverFactory.create(() => this.setDimensions());
        this.resizeObserver.observe(this.parentElement!);

        this.setDimensions();
    }

    public disconnectedCallback(): void {
        this.resizeObserver!.disconnect();
    }

    private setDimensions(): void {
        this.style.setProperty("--impulse-width", this.offsetWidth + "px");
        this.style.setProperty("--impulse-height", this.offsetHeight + "px");
    }
}


@customElement("eop-heroteaser-video-loop")
export class EopHeroTeaserVideoLoop extends PausableVideoLoop {

    public static readonly styles = Styles;

    private loading: Deferred<void>;

    public constructor() {
        super();
        this.loading = new Deferred();
    }

    public render(): TemplateResult {
        return html`
            <video @loadeddata=${this.visuallyReady} autoplay loop muted playsinline aria-label="">
                <source src=${this.src}>
            </video>
            ${this.renderPausePlayButton()}
        `;
    }

    public async whenVisuallyReady(): Promise<void> {
        return this.loading.promise;
    }

    private visuallyReady(): void {
        this.videoPlayingCallback();
        this.loading.resolve();
        this.classList.add("video-loaded");
    }
}

customElements.define("eop-heroteaser", EopHeroTeaser);
customElements.define("eop-impulse", EopImpulse);
